import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Clock, Target } from 'lucide-react';

const LAWYER_IMG = 'https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBsYXd5ZXIlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3NjExMjU0N3ww&ixlib=rb-4.1.0&q=85';

const highlights = [
  { icon: Award, label: 'Top Performance Trial Lawyers' },
  { icon: Target, label: 'Aggressive Courtroom Strategy' },
  { icon: Clock, label: 'Available 24/7 for Clients' },
];

export default function AboutSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 overflow-hidden" style={{ background: 'var(--color-surface)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Side */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -60 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="relative"
          >
            <div className="relative rounded-lg overflow-hidden">
              {/* REPLACE: lawyer meeting image with actual firm photo */}
              <img
                src={LAWYER_IMG}
                alt="Fitzgerald & Associates attorneys in a professional meeting"
                className="w-full h-[500px] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/40 to-transparent" />
            </div>
            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="absolute -bottom-6 -right-6 md:right-8 p-6 rounded-lg shadow-xl border"
              style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
            >
              <div className="text-3xl font-heading font-bold" style={{ color: 'var(--color-accent)' }}>10+</div>
              <div className="text-sm font-body" style={{ color: 'var(--color-text-muted)' }}>Years of<br />Excellence</div>
            </motion.div>
          </motion.div>

          {/* Text Side */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-4"
              style={{ background: 'var(--color-glow)', color: 'var(--color-accent)' }}
            >
              About Our Firm
            </span>
            <h2 data-testid="about-heading" className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-6" style={{ color: 'var(--color-text)' }}>
              Lawyers Here for You{' '}
              <span className="gradient-text">Through All Life Events</span>
            </h2>
            <p className="font-body text-base leading-relaxed mb-6" style={{ color: 'var(--color-text-muted)' }}>
              We are pleased to offer a diverse group of specialists who practice in virtually all
              areas of law. No matter the severity or scale of your legal inquiry, Fitzgerald &
              Associates attorneys can fully support you throughout the entire process.
            </p>
            <p className="font-body text-base leading-relaxed mb-8" style={{ color: 'var(--color-text-muted)' }}>
              Our commitment to protecting you and your loved one&apos;s rights, assets, freedoms
              and entitlements goes far beyond the trial process. We support you with intelligent
              due diligence, and experienced advice throughout every unique transaction, claim,
              contract and case we represent.
            </p>

            <div className="space-y-4 mb-8">
              {highlights.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3" data-testid={`about-highlight-${label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--color-glow)', color: 'var(--color-accent)' }}
                  >
                    <Icon size={18} />
                  </div>
                  <span className="font-body font-medium text-base" style={{ color: 'var(--color-text)' }}>{label}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-2">
                <Clock size={16} style={{ color: 'var(--color-accent)' }} />
                <span className="font-body text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Mon–Fri: 9AM – 6PM | Weekends: By Appointment
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
